import { useAtomValue } from "jotai";
import { locationInfoAtom, styleModeAtom } from "~/AtomWithDynamoDB";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";

type CustomVariableParams = {
  jaName?: string; // 日本語の場所名
  enName?: string; // 英語の場所名
};

/**
 * カスタム変数を利用した翻訳テキストを取得するhooks
 * パラメータで変数を指定しない場合、atomから取得する
 * 挨拶文、デフォルトメッセージなど場所情報を含むテキストに使用
 *
 * @param params カスタム変数のパラメータ（省略可能）
 */
export const useCustomVariables = (params?: CustomVariableParams) => {
  const { t } = useTranslation();
  const atomLocationInfo = useAtomValue(locationInfoAtom);
  const styleMode = useAtomValue(styleModeAtom);

  // パラメータが指定されていればそれを使用、なければatomから取得
  const jaName = params?.jaName ?? atomLocationInfo.jaName;
  const enName = params?.enName ?? atomLocationInfo.enName;

  // カスタム変数オブジェクトをメモ化
  const customVariables = useMemo(
    () => ({
      LOCATION_JA_NAME: jaName,
      LOCATION_EN_NAME: enName,
    }),
    [jaName, enName]
  );

  // t をラップして必ずcustomVariables を渡すようにする
  const tCustom = useCallback(
    <R = string>(key: string, options?: Record<string, unknown>) =>
      t(key, { ...customVariables, ...(options || {}) }) as R,
    [t, customVariables]
  );

  const greeting = useCallback(
    (options?: Record<string, unknown>) => tCustom(`standbyScreen.${styleMode}.greetings`, options),
    [tCustom, styleMode]
  );

  const defaultMessages = useCallback(
    (options?: Record<string, unknown>) =>
      tCustom<string[]>(`defaultMessage.${styleMode}`, { returnObjects: true, ...(options || {}) }),
    [tCustom, styleMode]
  );

  return { tCustom, greeting, defaultMessages };
};
